import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {RegulationNumberCell} from './components/RegulationsList/RegulationNumberCell';
import {
	renderArrayField,
	renderAttachments,
} from 'components/EntityList/ColumnRenderers';
import {renderKeywordsCell} from 'components/Keywords/Keywords';
import {
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListColumnMetaData,
} from 'components/InfinityList/InfinityList';
import {GetRegulationsQueryQuery} from './hooks/regulationsInfinity.generated';

import {parseTooltipNewlines} from 'helpers/tooltips';
import {
	getOptionsSorted,
	queryStringMongoArraySTD,
} from 'components/InfinityList/InfinitListStandardHooks';
import {alphabeticalSort, numericalSort} from 'helpers/sortHelpers';
import {GetAllKeywordsQuery} from 'features/AdminSection/hooks/keywords.generated';
import {GetAllMarketsQuery} from 'features/AdminSection/hooks/markets.generated';

export type IRegulationsPageFilterOptions = {
	(
		dataQuery: GetRegulationsQueryQuery | undefined,
		keywordData?: GetAllKeywordsQuery | undefined,
		marketData?: GetAllMarketsQuery | undefined,
	): InfinityListColumnFilterOptions[];
};

export const useRegulationsPageColumn = () => {
	const {t} = useTranslation('features/regulations', {
		keyPrefix: 'RegulationsList',
	});

	const {t: tt} = useTranslation('features/regulations', {
		keyPrefix: 'TooltipsText',
	});

	const widthSTD150 = {minWidth: 150, maxWidth: 200};
	const resizeMulti = {isResizable: true, isMultiline: true};
	const filterArraySimple = {
		filterOnFilter: true,
		filterType: 'Array',
	};
	const filterArray = {
		...filterArraySimple,
		onRender: renderArrayField(),
	};
	const filterArray150Simple = {
		...widthSTD150,
		...resizeMulti,
		...filterArraySimple,
	};
	const filterArray150 = {...widthSTD150, ...resizeMulti, ...filterArray};

	const columns: InfinityListColumn[] = useMemo(
		() => [
			{
				name: 'attachments',
				key: 'attachments',
				isIconOnly: true,
				iconName: 'Attach',
				fieldName: 'attachments',
				minWidth: 16,
				maxWidth: 16,
				onRender: renderAttachments(),
			},
			{
				key: 'regulationNumber',
				name: t('RegulationNumber'),
				fieldName: 'regulationNumber',
				...filterArray150Simple,
				onRender: RegulationNumberCell,
			},
			{
				key: 'name',
				name: t('Name'),
				fieldName: 'name',
				isMultiline: true,
				minWidth: 350,
				maxWidth: 450,
				isResizable: true,
			},

			{
				key: 'markets',
				name: t('Markets'),
				fieldName: 'markets',
				...filterArray150,
			},

			{
				key: 'mainKeywords',
				name: t('MainKeywords'),
				fieldName: 'mainKeywords',
				...filterArray150,
			},
			{
				key: 'keywords',
				name: t('KeyWords'),
				fieldName: 'keywords',
				...filterArray150Simple,
				onRender: renderKeywordsCell,
			},
			{
				key: 'standardPlusPcmsClusters',
				name: t('StandardPlusPcmsClusters'),
				fieldName: 'standardPlusPcmsClusters',
				...filterArray150Simple,
				onRender: renderArrayField(),
				tooltipHostProps: {
					content: parseTooltipNewlines(tt('standardPlusPcmsClusters')),
				},
			},
		],
		[],
	);

	return columns;
};

export const useRegulationsPageColumnMetaData = () => {
	const columnMetaData: InfinityListColumnMetaData[] = [
		{
			key: 'regulationNumber',
			mongoDBFn: queryStringMongoArraySTD('_id', true),
			linkName: 'id',
		},
		{
			key: 'markets',
			mongoDBFn: queryStringMongoArraySTD('marketRefs._id'),
		},
		{
			key: 'mainKeywords',
			mongoDBFn: queryStringMongoArraySTD('mainKeywordRefs._id'),
		},
		{
			key: 'keywords',
			mongoDBFn: queryStringMongoArraySTD('keywordRefs._id'),
		},
		{
			key: 'standardPlusPcmsClusters',
			mongoDBFn: queryStringMongoArraySTD('standardPlusPcmsClusterRefs._id'),
		},
	];
	return columnMetaData;
};

export const useRegulationsPageFilterOptions: IRegulationsPageFilterOptions = (
	dataQuery,
	keywordData,
	marketData,
) => {
	const columnFilterOptions: InfinityListColumnFilterOptions[] = useMemo(() => {
		const queryData = dataQuery?.regulationsQuery.regulationFilterOptions;
		const marketsAll = marketData?.markets ?? [];
		const keywordsAll = keywordData?.keywords ?? [];

		const marketOptions = queryData?.marketOptions ?? [];
		const mainKeywordOptions = queryData?.mainKeywordOptions ?? [];
		const keywordOptions = queryData?.keywordOptions ?? [];

		const marketOptionsSorted = getOptionsSorted(marketsAll, marketOptions);

		const mainKeywordOptionsSorted = getOptionsSorted(
			keywordsAll,
			mainKeywordOptions,
		);

		const keywordOptionsSorted = getOptionsSorted(keywordsAll, keywordOptions);

		const filterOptions = [
			{
				key: 'regulationNumber',
				fieldValuesUnique: queryData?.regulationOptions,
			},
			{
				key: 'markets',
				fieldValuesUnique: marketOptionsSorted,
			},
			{
				key: 'mainKeywords',
				fieldValuesUnique: mainKeywordOptionsSorted,
			},
			{
				key: 'keywords',
				fieldValuesUnique: keywordOptionsSorted,
			},
			{
				key: 'standardPlusPcmsClusters',
				fieldValuesUnique: alphabeticalSort(
					queryData?.pcmsOptions ?? [],
					'name',
				),
			},
		];
		return filterOptions.map(x => {
			return {...x, fieldValuesUnique: x.fieldValuesUnique ?? []};
		});
	}, [dataQuery, keywordData, marketData]);
	return columnFilterOptions;
};
